import React from "react";
import { FileText, User, Clock, AlertTriangle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

const Documents = () => {
  const documentSections = [
    {
      title: "Fresh Passport",
      icon: <FileText className="h-6 w-6 text-blue-600" />,
      documents: [
        "Proof of Date of Birth (Birth Certificate / School Leaving Certificate)",
        "Proof of Present Address (Aadhaar Card / Voter ID / Utility Bill)",
        "Identity Proof (PAN Card / Driving License)",
        "Photographs will be captured at passport office"
      ]
    },
    {
      title: "Passport Renewal",
      icon: <Clock className="h-6 w-6 text-green-600" />,
      documents: [
        "Old Passport (Original)",
        "Photocopy of Passport's first and last page",
        "Proof of Current Address (if changed)",
        "Photographs will be captured at passport office"
      ]
    },
    {
      title: "Lost/Damaged Passport",
      icon: <AlertTriangle className="h-6 w-6 text-red-600" />,
      documents: [
        "Police Complaint/FIR copy",
        "Self-attested photocopy of lost/damaged passport (if available)",
        "Proof of Identity",
        "Proof of Address"
      ]
    },
    {
      title: "Minor Passport",
      icon: <User className="h-6 w-6 text-purple-600" />,
      documents: [
        "Birth Certificate of the minor",
        "Address Proof of parents",
        "Passport copies of parents (if available)",
        'Annexure "D" (Parental consent form)'
      ]
    }
  ];

  const generalNotes = [
    "All documents should be original with self-attested photocopies",
    "Documents in regional languages should be translated to English",
    "Photographs will be taken at the Passport Seva Kendra",
    "Additional documents may be required based on individual cases"
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Required Documents</h1>
          <p className="text-xl text-gray-600">
            Complete checklist of documents needed for your passport application
          </p>
        </div>

        {/* Documents by Category */}
        <div className="mb-12">
          <Accordion type="single" collapsible className="space-y-4">
            {documentSections.map((section, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="bg-white rounded-lg border shadow-sm">
                <AccordionTrigger className="px-6 py-4 hover:no-underline">
                  <div className="flex items-center space-x-3">
                    {section.icon}
                    <span className="text-lg font-semibold">{section.title}</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-4">
                  <ul className="space-y-3">
                    {section.documents.map((doc, docIndex) => (
                      <li key={docIndex} className="flex items-start space-x-3">
                        <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-gray-700">{doc}</span>
                      </li>
                    ))}
                  </ul>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        {/* General Notes */}
        <Card className="bg-blue-50 border-blue-200">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2 text-blue-800">
              <AlertTriangle className="h-5 w-5" />
              <span>Important Notes</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {generalNotes.map((note, index) => (
                <li key={index} className="flex items-start space-x-3 text-blue-700">
                  <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                  <span>{note}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {/* Help Section */}
        <div className="mt-12 text-center bg-white rounded-lg p-8 shadow-md">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Need Document Assistance?</h2>
          <p className="text-gray-600 mb-6">
            Our experts can help you prepare and verify all required documents for a smooth application process.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="https://wa.me/917977084003" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-green-600 hover:bg-green-700 transition-colors"
            >
              Get Help via WhatsApp
            </a>
            <a 
              href="tel:+917977084003"
              className="inline-flex items-center justify-center px-6 py-3 border border-gray-300 text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
            >
              Call for Assistance
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Documents;